<script setup lang="ts">
import { computed, useSlots } from 'vue'
import PrimeButton from 'primevue/button'

type ButtonSeverity =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'quaternary'
  | 'warn'
  | 'help'
  | 'danger'
  | 'contrast'

type ButtonVariant = 'filled' | 'outlined' | 'text'
type ButtonSize = 'small' | 'large'

interface Props {
  label?: string
  severity?: ButtonSeverity
  variant?: ButtonVariant
  loading?: boolean
  size?: ButtonSize
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  icon?: string
  iconPos?: 'left' | 'right'
  buttonClass?: string
  pt?: object
  ptOptions?: object
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  severity: 'primary',
  variant: 'filled',
  loading: false,
  size: 'small',
  disabled: false,
  type: 'button',
  iconPos: 'left',
  buttonClass:
    '!montserrat w-full !rounded-lg flex items-center justify-center gap-2 !px-6 !min-w-[120px]',
})

defineOptions({ inheritAttrs: false })

const slots = useSlots()

const isDisabled = computed(() => props.loading || props.disabled)

const hasCustomIcon = computed(() => !!slots.icon)

const resolvedVariant = computed(() => {
  if (props.severity === 'secondary') return 'outlined'
  return props.variant
})
</script>

<template>
  <PrimeButton
    :label="!hasCustomIcon ? props.label : undefined"
    :severity="props.severity"
    :variant="resolvedVariant"
    :loading="props.loading"
    :size="props.size"
    :disabled="isDisabled"
    :type="props.type"
    :icon="!hasCustomIcon ? props.icon : undefined"
    :iconPos="props.iconPos"
    :pt="props.pt"
    :ptOptions="props.ptOptions"
    v-bind="$attrs"
    :class="[
      '!border-solid !border !border-surface-200/80 dark:!border-surface-700 bg-surface-0 dark:bg-surface-800 shadow-xs transition-all duration-200 active:translate-y-px disabled:!cursor-not-allowed disabled:opacity-55 disabled:shadow-none',

      props.severity === 'primary' &&
        '!bg-[var(--p-primary-color)] hover:!bg-[var(--p-primary-hover-color)] !text-white !border-[var(--p-primary-color)] shadow-xs hover:shadow-sm',

      props.severity === 'secondary' &&
        '!bg-transparent !text-[var(--p-primary-color)] dark:!text-[var(--p-primary-100)] !border-[var(--p-primary-color)] hover:!bg-[var(--p-primary-50)] dark:hover:!bg-[var(--p-primary-900)]/30 shadow-xs',

      props.severity === 'tertiary' &&
        '!bg-[var(--p-primary-50)] !text-[var(--p-primary-color)] !border-[var(--p-primary-200)] hover:!bg-[var(--p-primary-100)] dark:!bg-[var(--p-primary-950)]/40 dark:!text-[var(--p-primary-100)] dark:!border-[var(--p-primary-800)] dark:hover:!bg-[var(--p-primary-900)]/60',

      props.severity === 'quaternary' &&
        '!bg-transparent hover:!bg-[var(--p-primary-50)] dark:hover:!bg-[var(--p-primary-900)]/30 !border-transparent !text-[var(--p-primary-color)] dark:!text-[var(--p-primary-100)]',

      props.buttonClass,
    ]"
  >
    <!-- Si hay icon custom -->
    <template v-if="hasCustomIcon">
      <span v-if="props.iconPos === 'left'" class="flex items-center">
        <slot name="icon" />
      </span>

      <span v-if="props.label">
        {{ props.label }}
      </span>

      <span v-if="props.iconPos === 'right'" class="flex items-center">
        <slot name="icon" />
      </span>
    </template>
  </PrimeButton>
</template>

<style scoped>
:deep(.p-button) {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Montserrat';
  /* margin-top: 15px; */
}

/* :deep(.p-button:hover) {
  background-color: rgba(var(--p-primary-color-rgb), 0.1) !important;
  border-color: var(--p-primary-color) !important;
  opacity: 1 !important;
}  */
</style>
